import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ArrowRight, Sparkles, TrendingUp, Home, Calendar, User, Users, CheckCircle2, MapPin, Star } from 'lucide-react';
import Header from '@/components/customer/Header';

import ServiceCard from '@/components/customer/ServiceCard';
import ServiceCardSkeleton from '@/components/customer/ServiceCardSkeleton';
import SubServiceItem from '@/components/customer/SubServiceItem';
import { useInfiniteServices } from '@/hooks/useServices';
import { serviceImages } from '@/lib/service-images';
import { motion, AnimatePresence } from 'framer-motion';
import heroBannerDefault from '@/assets/hero-banner.jpg';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCityPricing } from '@/hooks/useCityPricing';
import { useBannerSlides } from '@/hooks/useBannerSlides';
import { useFavorites } from '@/hooks/useFavorites';
import { useHomeInit } from '@/hooks/useHomeInit';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import { ServiceCategory } from '@/lib/types';
import { useDebounce } from '@/hooks/useDebounce';

interface SearchResult extends ServiceCategory {
  matchType?: 'category' | 'subservice';
  matchedSub?: {
    id: string;
    name: string;
    description: string;
    price: number;
    duration: string;
  } | null;
}

const CategoryIcon = memo(({ s, serviceImages }: { s: ServiceCategory, serviceImages: Record<string, string> }) => {
  const [error, setError] = useState(false);
  const image = (!error && (s.imageUrl || serviceImages[s.id] || serviceImages[s.slug || ''])) || null;

  return (
    <motion.div whileTap={{ scale: 0.9 }} whileHover={{ scale: 1.1, y: -4 }}
      className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-card border border-border/50 shadow-card flex items-center justify-center overflow-hidden">
      {image ? (
        <img 
          src={image} 
          alt={s.name} 
          onError={() => setError(true)}
          className="h-11 w-11 md:h-14 md:w-14 object-contain filter saturate-[1.15] brightness-[1.05] drop-shadow-md" 
        />
      ) : (
        <span className="text-2xl md:text-3xl">{s.icon}</span>
      )}
    </motion.div>
  );
});

CategoryIcon.displayName = 'CategoryIcon';

const Index = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const displaySearchQuery = useDebounce(debouncedSearchQuery, 0); // Still debounced to match results
  const { activeSlides, loading: slidesLoading, slideInterval } = useBannerSlides();
  const { 
    services: enrichedServices, 
    loading: servicesLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteServices();
  const observerRef = useRef<HTMLDivElement | null>(null);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [detectedCity, setDetectedCity] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const { data: activeCities = [] } = useQuery({
    queryKey: ['activeCities'],
    queryFn: async () => {
      const { data } = await supabase.from('cities').select('id, name, marketing_discount_percent').eq('is_active', true);
      return (data || []) as { id: string; name: string, marketing_discount_percent: number | null }[];
    },
    staleTime: 60 * 60 * 1000 // 1 hour cache
  });

  // Auto-slide with dynamic interval — pauses on touch/hover
  useEffect(() => {
    if (activeSlides.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % activeSlides.length);
    }, slideInterval);
    return () => clearInterval(interval);
  }, [activeSlides.length, slideInterval, isPaused]);

  const pauseAutoSlide = useCallback(() => {
    setIsPaused(true);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 5000);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50 && activeSlides.length > 1) {
      if (diff > 0) {
        setCurrentSlide(prev => (prev + 1) % activeSlides.length);
      } else {
        setCurrentSlide(prev => (prev - 1 + activeSlides.length) % activeSlides.length);
      }
    }
    pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 5000);
  }, [activeSlides.length]);

  const handleCityDetected = useCallback((city: string) => {
    setDetectedCity(city);
  }, []);

  // Unified pricing hook — single source of truth (eliminates 50+ lines of duplicated logic)
  // Same hook used by ServiceDetail and SubServiceDetail → pricing always consistent
  const { enrichSubService, cityPrices } = useCityPricing();

  const pricedServices = useMemo(() => {
    return enrichedServices.map(s => ({
      ...s,
      subServices: s.subServices.map(sub => enrichSubService(sub)),
      startingPrice: s.subServices.length > 0
        ? Math.min(...s.subServices.map(sub => {
            const cityData = cityPrices[sub.id];
            return cityData ? cityData.price : sub.price;
          }))
        : s.startingPrice,
    }));
  }, [enrichedServices, enrichSubService, cityPrices]);

  const pricedServicesMap = useMemo(() => {
    const map = new Map<string, typeof pricedServices[0]>();
    pricedServices.forEach(s => map.set(s.id, s));
    return map;
  }, [pricedServices]);

  const matchedCity = useMemo(() => {
    if (!detectedCity) return undefined;
    return activeCities.find(c =>
      detectedCity.toLowerCase().includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(detectedCity.toLowerCase())
    );
  }, [detectedCity, activeCities]);

  const isCityActive = useMemo(() => {
    if (!detectedCity) return true;
    return activeCities.length === 0 || activeCities.some(c =>
      detectedCity.toLowerCase().includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(detectedCity.toLowerCase())
    );
  }, [detectedCity, activeCities]);

  const isSearching = debouncedSearchQuery.trim().length > 0;
  const { data: searchResults = [], isLoading: searchLoading } = useQuery({
    queryKey: ['global-search', debouncedSearchQuery.trim()],
    queryFn: async () => {
      const q = debouncedSearchQuery.trim();
      if (!q) return [];
      const { data, error } = await supabase.rpc('global_search' as any, { query_text: q });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: isSearching,
    staleTime: 500,
    gcTime: 5 * 60 * 1000
  });

  const cityFilteredServices = useMemo(() => {
    return matchedCity
      ? pricedServices.filter(s => s.cityIds.length === 0 || s.cityIds.includes(matchedCity.id))
      : pricedServices;
  }, [matchedCity, pricedServices]);

  const filteredServices = useMemo(() => {
    if (!isSearching) return cityFilteredServices as SearchResult[];
    
    // Merge RPC results with full service data to keep Pricing/Logic intact
    return searchResults.map(res => {
      const fullService = pricedServicesMap.get(res.id);
      if (!fullService || (matchedCity && fullService.cityIds.length > 0 && !fullService.cityIds.includes(matchedCity.id))) return null;
      
      const isSubMatch = res.match_type?.toLowerCase() === 'subservice';
      
      const result: SearchResult = {
        ...fullService,
        matchType: isSubMatch ? 'subservice' : 'category',
        matchedSub: isSubMatch ? {
          id: res.matched_subservice_id,
          name: res.matched_subservice_name,
          description: res.matched_subservice_description || '',
          price: Number(res.matched_subservice_price) || 0,
          duration: res.matched_subservice_duration || '30-45 mins'
        } : null
      };
      return result;
    }).filter(Boolean) as SearchResult[];
  }, [isSearching, searchResults, cityFilteredServices]);

  const slide = activeSlides[currentSlide];
  const heroImg = slide?.image_url || heroBannerDefault;

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    if (!hasNextPage || servicesLoading) return;
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    }, { threshold: 0.1 });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, servicesLoading]);

  return (
    <div className="min-h-screen gradient-mesh pb-4 lg:pb-8">
      <Header onCityDetected={handleCityDetected} />

      {/* Desktop sidebar nav */}
      <div className="hidden lg:fixed lg:block lg:left-0 lg:top-16 lg:bottom-0 lg:w-56 lg:border-r lg:bg-card/50 lg:backdrop-blur-sm lg:p-4 lg:z-40">
        <nav className="space-y-1 mt-2">
          {[
            { label: 'Home', path: '/', icon: Home },
            { label: 'Search', path: '/search', icon: Search },
            { label: 'Bookings', path: '/bookings', icon: Calendar },
            { label: 'Profile', path: '/profile', icon: User },
          ].map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-6 lg:ml-56 space-y-5 md:space-y-6">
        {/* City not active warning */}
        {(sessionStorage.getItem('is_unserviceable') === 'true' || (detectedCity && !isCityActive)) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl bg-warning border-2 border-warning/20 p-5 shadow-lg relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-3 opacity-10">
              <MapPin className="h-12 w-12" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-warning-foreground" />
                <h3 className="text-sm font-bold text-warning-foreground uppercase tracking-tight">Expansion Alert</h3>
              </div>
              <p className="text-sm font-bold text-warning-foreground leading-tight">
                We are coming soon to: <span className="underline decoration-2 underline-offset-2">{detectedCity}</span>
              </p>
              <p className="text-[11px] text-warning-foreground/80 mt-1 font-medium">
                Services in your area will be available shortly. Stay tuned!
              </p>
            </div>
          </motion.div>
        )}

        {/* --- PREVIEW SKELETON LOADER --- */}

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="relative max-w-xl"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search for services..."
            className="w-full h-12 md:h-14 pl-11 md:pl-12 pr-4 rounded-2xl bg-card shadow-card border border-border/50 text-sm md:text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:shadow-elevated transition-all duration-300"
          />
        </motion.div>

        {isSearching ? (
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-xs md:text-sm text-muted-foreground">
                {searchLoading ? 'Scanning services...' : `${filteredServices.length} result${filteredServices.length !== 1 ? 's' : ''} found`}
              </p>
              {!searchLoading && <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full uppercase">Professional Search</span>}
            </div>

            {searchLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {[...Array(4)].map((_, i) => <ServiceCardSkeleton key={i} />)}
              </div>
            ) : filteredServices.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {filteredServices.map((s, i) => {
                  if (s.matchType === 'subservice' && s.matchedSub) {
                    return (
                      <SubServiceItem 
                        key={s.matchedSub.id}
                        sub={s.matchedSub as any}
                        serviceId={s.id}
                        serviceName={s.name}
                        serviceSlug={s.slug || s.id}
                        index={i}
                      />
                    );
                  }
                  return (
                    <div key={s.id} className="relative group max-w-sm">
                      <ServiceCard 
                        service={s} 
                        index={i} 
                        isFavorite={isFavorite(s.id)} 
                        onToggleFavorite={toggleFavorite}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                <p className="text-4xl mb-3">🔍</p>
                <p className="text-sm md:text-base text-muted-foreground">No services found for "{debouncedSearchQuery}"</p>
              </motion.div>
            )}
          </section>
        ) : (
          <>
            {/* Announcements */}
            <div className="mb-4">
              <AnnouncementBanner />
            </div>

            {/* Hero Banner Carousel */}
            <div
              className="relative overflow-hidden rounded-3xl h-44 md:h-56 lg:h-64 shadow-elevated cursor-grab active:cursor-grabbing"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => { pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 2000); }}
            >
              <AnimatePresence mode="wait">
                {slide && (
                  <motion.div
                    key={slide.id}
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -60 }}
                    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="absolute inset-0"
                  >
                    <img src={slide.image_url || heroBannerDefault} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 via-foreground/50 to-foreground/10" />
                    <div className="relative z-10 h-full flex flex-col justify-center p-5 md:p-8 lg:p-10">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4 text-warning" />
                        <p className="text-[11px] md:text-xs font-semibold text-primary-foreground/80 uppercase tracking-wider">Limited Offer</p>
                      </div>
                      <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-1 md:mb-2">{slide.title}</h2>
                      <p className="text-xs md:text-sm text-primary-foreground/70 mb-3 md:mb-4 max-w-md">{slide.subtitle}</p>
                      {slide.show_button !== false && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => navigate(slide.button_link || '/search')}
                          className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs md:text-sm font-semibold px-5 md:px-6 py-2.5 md:py-3 rounded-full shadow-primary-glow w-fit"
                        >
                          {slide.button_text} <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Dots indicator */}
              {activeSlides.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                  {activeSlides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { setCurrentSlide(i); pauseAutoSlide(); }}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === currentSlide ? 'w-6 bg-primary-foreground' : 'w-1.5 bg-primary-foreground/40'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Quick Categories */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.4 }}>
              <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1 lg:flex-wrap">
                {(servicesLoading) ? (
                   [...Array(6)].map((_, i) => (
                    <div key={i} className="flex flex-col items-center gap-1.5 md:gap-2 min-w-[64px] md:min-w-[72px]">
                      <div className="h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-muted animate-pulse"></div>
                      <div className="h-2 w-10 bg-muted animate-pulse rounded"></div>
                    </div>
                  ))
                ) : (cityFilteredServices).map((s, i) => (
                  <motion.div key={s.id} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 + i * 0.04 }}>
                    <Link to={`/service/${s.slug || s.id}`} className="flex flex-col items-center gap-1.5 md:gap-2 min-w-[64px] md:min-w-[72px]">
                      <CategoryIcon s={s} serviceImages={serviceImages} />
                      <span className="text-[10px] md:text-[11px] font-medium text-muted-foreground text-center whitespace-nowrap">{s.name}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Stats bar */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="flex items-center gap-3 md:gap-4 overflow-x-auto scrollbar-hide">
              {[
                { label: 'Happy Users', value: '12K+', icon: Users, color: 'text-primary' },
                { label: 'Services Done', value: '45K+', icon: CheckCircle2, color: 'text-success' },
                { label: 'Cities', value: '6+', icon: MapPin, color: 'text-info' },
                { label: 'Rating', value: '4.8★', icon: Star, color: 'text-warning' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2.5 min-w-fit bg-card rounded-2xl px-4 py-2.5 shadow-card border border-border/50">
                  <div className={`h-8 w-8 rounded-lg bg-muted flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-bold leading-tight">{stat.value}</p>
                    <p className="text-[10px] md:text-[11px] text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* All Services */}
            <section>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
                className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base md:text-lg lg:text-xl font-bold">All Services</h2>
                  <p className="text-[11px] md:text-xs text-muted-foreground">
                    {cityFilteredServices.length} services available {detectedCity ? `in ${detectedCity}` : 'near you'}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-primary font-medium">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Popular
                </div>
              </motion.div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {servicesLoading ? (
                  [...Array(8)].map((_, i) => <ServiceCardSkeleton key={i} />)
                ) : (
                  <>
                    {cityFilteredServices.map((s, i) => (
                      <ServiceCard key={s.id} service={s} index={i} isFavorite={isFavorite(s.id)} onToggleFavorite={toggleFavorite} />
                    ))}
                    {isFetchingNextPage && (
                      [...Array(4)].map((_, i) => <ServiceCardSkeleton key={`next-${i}`} />)
                    )}
                  </>
                )}
              </div>
              
              {/* Observer Trigger */}
              <div ref={observerRef} className="h-20 flex items-center justify-center mt-4">
                {isFetchingNextPage && <p className="text-xs text-muted-foreground animate-pulse">Loading more services...</p>}
              </div>
            </section>
          </>
        )}
      </main>


    </div>
  );
};

export default Index;
